/**
 * Batch upload context — lifts upload state to app level.
 *
 * Wraps useBatchUpload() so the upload queue persists across page
 * navigation. Both the Documents and DataSources pages share the
 * same queue via useBatchUploadContext().
 *
 * The floating BatchUploadPanel renders inside this provider as a
 * React portal, so it survives route transitions.
 */

import BatchUploadPanel from '@/components/upload/BatchUploadPanel';
import { useBatchUpload } from '@/hooks/useBatchUpload';
import type { UseBatchUploadReturn } from '@/hooks/useBatchUpload';
import { AnimatePresence } from 'framer-motion';
import React, { createContext, useContext } from 'react';

const BatchUploadContext = createContext<UseBatchUploadReturn | null>(null);

export const BatchUploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const batchUpload = useBatchUpload();

  return (
    <BatchUploadContext.Provider value={batchUpload}>
      {children}
      <AnimatePresence>
        {batchUpload.isPanelVisible && (
          <BatchUploadPanel
            key="batch-upload-panel"
            items={batchUpload.items}
            stats={batchUpload.stats}
            isActive={batchUpload.isActive}
            isPanelMinimized={batchUpload.isPanelMinimized}
            onCancelItem={batchUpload.cancelItem}
            onCancelAll={batchUpload.cancelAll}
            onRemoveItem={batchUpload.removeItem}
            onClearCompleted={batchUpload.clearCompleted}
            onRetryItem={batchUpload.retryItem}
            onToggleMinimize={batchUpload.toggleMinimize}
            onHidePanel={batchUpload.hidePanel}
          />
        )}
      </AnimatePresence>
    </BatchUploadContext.Provider>
  );
};

export function useBatchUploadContext(): UseBatchUploadReturn {
  const ctx = useContext(BatchUploadContext);
  if (!ctx) {
    throw new Error('useBatchUploadContext must be used within BatchUploadProvider');
  }
  return ctx;
}
